import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Review } from '@app/shared/models/review.model';
import { TableColumn } from '@app/shared/models/table-column.model';
import { EllipsisDirective } from '@app/shared/directives/ellipsis';

type TableRow = Review;

@Component({
  selector: 'app-shared-table',
  exportAs: 'sharedTable',
  imports: [
    CommonModule,
    MatTableModule,
    MatSort,
    MatSortModule,
    MatTooltip,
    EllipsisDirective,
  ],
  templateUrl: './table.html',
  styleUrl: './table.scss',
})
export class TableComponent implements AfterViewInit, OnChanges {
  @Input() dataSource!: MatTableDataSource<TableRow>;
  @Input() columns: TableColumn<TableRow>[] = [];
  @Input() displayedColumns: string[] = [];
  @Input() enableSort = true;
  @Input() selectedRow?: TableRow | null;

  @Output() rowClick = new EventEmitter<TableRow>();

  trackByRowId = (_: number, row: Review): string => row.id;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('tableContainer') tableContainerRef?: ElementRef<HTMLDivElement>;
  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    const target = (event.target as HTMLElement).closest('tr');
    if (target?.classList.contains('mat-mdc-row')) {
      target.classList.add('mouse-focus-removed');
    }
  }

  ngAfterViewInit(): void {
    this.attachSortIfEnabled();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource']) {
      this.attachSortIfEnabled();
    }
  }

  private attachSortIfEnabled(): void {
    if (this.enableSort && this.dataSource && this.sort) {
      const defaultAccessor = this.dataSource.sortingDataAccessor;
      this.dataSource.sortingDataAccessor = (
        item: TableRow,
        property: string,
      ) => {
        if (property === 'approverName') {
          return (item.approver?.name ?? '').toLowerCase();
        }
        return defaultAccessor(item, property);
      };
      this.dataSource.sort = this.sort;
    }
  }

  onRowClick(row: TableRow): void {
    this.rowClick.emit(row);
  }

  public scrollToRowById(id: string): void {
    const container = this.tableContainerRef?.nativeElement;
    if (!container) return;
    const rowEl = container.querySelector<HTMLElement>(
      `tr[data-row-id="${id}"]`,
    );
    if (!rowEl) return;

    const rowTop = rowEl.offsetTop;
    const rowBottom = rowTop + rowEl.offsetHeight;
    const visibleTop = container.scrollTop;
    const visibleBottom = visibleTop + container.clientHeight;

    if (rowTop < visibleTop) {
      container.scrollTop = rowTop;
    } else if (rowBottom > visibleBottom) {
      container.scrollTop = rowBottom - container.clientHeight;
    }
  }
}
